import React, { useState } from "react";
import FieldHelp from "../help/FieldHelp";

/**
 * Editor for an Environment (Temperature & Humidity) report email schedule,
 * matching the backend shape:
 *   [{ label, meter_ids: [...], emails: [...], days: [...], weekly_day, windows: [{ start, end, last_sent }] }]
 *
 * - "meter_ids": [] = All sensors; or [meter_id] for a specific sensor.
 * - "days":      [1] = Daily, [7] = Weekly, [15] = 15 Days, [30] = Monthly.
 * - "windows":   24-hour "HH:MM" time ranges.
 *
 * Fully controlled: never mutates `value`, calls `onChange` with the next array.
 */

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || "").trim());

// Report period presets (multi-select).
const PERIOD_OPTIONS = [
  { label: "Daily", value: 1 },
  { label: "Weekly", value: 7 },
  { label: "15 Days", value: 15 },
  { label: "Monthly", value: 30 },
];

const WEEKDAY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const newWindow = () => ({ start: "09:00", end: "17:00", last_sent: null });
const newEnvGroup = () => ({
  sensorId: "ALL",
  label: "ALL",
  meter_ids: [],
  emails: [],
  days: [1],
  weekly_day: "Monday",
  windows: [newWindow()],
});

export default function EnvironmentScheduleEditor({
  value,
  onChange,
  onSave,
  saving = false,
  loading = false,
  sensors = [],
  sensorsLoading = false,
  styles = {},
  emptyText = "No environment schedules configured yet. Click “Add Sensor Schedule” to create one.",
  onInvalidEmail,
}) {
  const {
    inputGroupStyle = {},
    textInputStyle = {},
    primaryButtonStyle = {},
    actionButtonStyle = {},
  } = styles;

  const [emailInput, setEmailInput] = useState({}); // per-group transient text

  const groups = Array.isArray(value) ? value : [];

  const mutate = (gidx, fn) =>
    onChange(groups.map((g, i) => (i === gidx ? fn(g) : g)));

  const addGroup = () => onChange([...groups, newEnvGroup()]);
  const removeGroup = (gidx) => onChange(groups.filter((_, i) => i !== gidx));

  const setSensorTarget = (gidx, targetValue) => {
    mutate(gidx, (g) => {
      if (targetValue === "ALL" || !targetValue) {
        return {
          ...g,
          sensorId: "ALL",
          label: "ALL",
          meter_ids: [],
        };
      }
      const found = sensors.find((s) => s.meter_id === targetValue);
      const name = found ? found.location_name || found.machine_name || found.meter_id : targetValue;
      return {
        ...g,
        sensorId: targetValue,
        label: name,
        meter_ids: [targetValue],
      };
    });
  };

  const togglePeriod = (gidx, periodVal) =>
    mutate(gidx, (g) => {
      const currentDays = (Array.isArray(g.days) ? g.days : (g.days ? [g.days] : [])).map(Number);
      const set = new Set(currentDays);
      if (set.has(periodVal)) set.delete(periodVal);
      else set.add(periodVal);
      return { ...g, days: Array.from(set).sort((a, b) => a - b) };
    });

  const setWeeklyDay = (gidx, weekly_day) =>
    mutate(gidx, (g) => ({ ...g, weekly_day }));

  const addWindow = (gidx) =>
    mutate(gidx, (g) => ({ ...g, windows: [...g.windows, newWindow()] }));

  const removeWindow = (gidx, widx) =>
    mutate(gidx, (g) => ({ ...g, windows: g.windows.filter((_, j) => j !== widx) }));

  const setWindow = (gidx, widx, field, val) =>
    mutate(gidx, (g) => ({
      ...g,
      windows: g.windows.map((w, j) => (j === widx ? { ...w, [field]: val } : w)),
    }));

  const addEmail = (gidx) => {
    const raw = (emailInput[gidx] || "").trim();
    if (!raw) return;
    if (!isValidEmail(raw)) {
      onInvalidEmail && onInvalidEmail();
      return;
    }
    mutate(gidx, (g) =>
      g.emails.includes(raw) ? g : { ...g, emails: [...g.emails, raw] },
    );
    setEmailInput((prev) => ({ ...prev, [gidx]: "" }));
  };

  const removeEmail = (gidx, email) =>
    mutate(gidx, (g) => ({ ...g, emails: g.emails.filter((x) => x !== email) }));

  if (loading) {
    return <p style={{ color: "#64748b", fontSize: 14 }}>Loading environment schedule...</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {groups.length === 0 && (
        <p style={{ color: "#94a3b8", fontSize: 14 }}>{emptyText}</p>
      )}

      {groups.map((group, gidx) => {
        const currentTarget =
          group.sensorId ||
          (Array.isArray(group.meter_ids) && group.meter_ids.length === 1
            ? group.meter_ids[0]
            : "ALL");

        return (
          <div
            key={gidx}
            style={{
              border: "1px solid #e2e8f0",
              borderRadius: 10,
              padding: 14,
              background: "#f8fafc",
            }}
          >
            {/* Sensor Selection row */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label style={{ ...inputGroupStyle, flex: "1 1 240px", minWidth: 200 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 4, display: "block" }}>
                  Target Sensor
                </span>
                <select
                  value={currentTarget}
                  onChange={(e) => setSensorTarget(gidx, e.target.value)}
                  disabled={sensorsLoading}
                  style={{ ...textInputStyle, cursor: "pointer", width: "100%" }}
                >
                  <option value="ALL">All Sensors (Whole Facility)</option>
                  {sensors.map((s) => (
                    <option key={s.meter_id} value={s.meter_id}>
                      {s.location_name || s.machine_name || s.meter_id} ({s.meter_id})
                    </option>
                  ))}
                </select>
              </label>
              <div style={{ marginLeft: "auto" }}>
                <button
                  type="button"
                  onClick={() => removeGroup(gidx)}
                  style={{ ...actionButtonStyle, color: "#ef4444" }}
                >
                  Remove Schedule
                </button>
              </div>
            </div>

            {/* Recipients */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                Recipients
              </div>
              {group.emails.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {group.emails.map((email) => (
                    <span
                      key={email}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "4px 10px",
                        background: "#e6f7f5",
                        color: "#0f766e",
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 500,
                      }}
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => removeEmail(gidx, email)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#0f766e",
                          cursor: "pointer",
                          fontSize: 14,
                          lineHeight: 1,
                          padding: 0,
                        }}
                        aria-label={`Remove ${email}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input
                  type="email"
                  value={emailInput[gidx] || ""}
                  onChange={(e) => setEmailInput((prev) => ({ ...prev, [gidx]: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addEmail(gidx);
                    }
                  }}
                  placeholder="Add recipient email"
                  style={{ ...textInputStyle, flex: "1 1 220px", minWidth: 200 }}
                />
                <button
                  type="button"
                  onClick={() => addEmail(gidx)}
                  style={{ ...primaryButtonStyle, padding: "8px 16px" }}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Report period */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                Report Period
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {PERIOD_OPTIONS.map((opt) => {
                  const active = (group.days || []).includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => togglePeriod(gidx, opt.value)}
                      aria-pressed={active}
                      style={{
                        padding: "5px 12px",
                        borderRadius: 999,
                        border: active ? "1px solid #1BA08E" : "1px solid #cbd5e1",
                        background: active ? "#1BA08E" : "#fff",
                        color: active ? "#fff" : "#475569",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {/* Weekly send day selector */}
              {(group.days || []).includes(7) && (
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 500, color: "#475569" }}>
                    Send weekly on:
                  </span>
                  <select
                    value={group.weekly_day || "Monday"}
                    onChange={(e) => setWeeklyDay(gidx, e.target.value)}
                    style={{
                      ...textInputStyle,
                      width: "auto",
                      padding: "4px 10px",
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: "pointer",
                      borderRadius: 6,
                      border: "1px solid #cbd5e1",
                      background: "#ffffff",
                      color: "#1e293b",
                    }}
                  >
                    {WEEKDAY_OPTIONS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Time windows - ONLY FOR DAILY */}
            {(group.days || []).includes(1) ? (
              <div style={{ marginTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                    Daily Time Windows
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => {
                        const wins = (group.windows && group.windows.length > 0) ? group.windows : [newWindow()];
                        mutate(gidx, (g) => ({
                          ...g,
                          windows: [{ ...wins[0], start: "00:00", end: "23:59" }],
                        }));
                      }}
                      style={{
                        padding: "3px 8px",
                        fontSize: 11,
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        background: "#fff",
                        color: "#0f766e",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      title="Set full calendar day (00:00 to 23:59)"
                    >
                      ⚡ 24h Full Day (00:00 - 23:59)
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const wins = (group.windows && group.windows.length > 0) ? group.windows : [newWindow()];
                        mutate(gidx, (g) => ({
                          ...g,
                          windows: [{ ...wins[0], start: "09:00", end: "09:00" }],
                        }));
                      }}
                      style={{
                        padding: "3px 8px",
                        fontSize: 11,
                        borderRadius: 6,
                        border: "1px solid #cbd5e1",
                        background: "#fff",
                        color: "#0f766e",
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                      title="Set 24h rolling window (Yesterday 09:00 to Today 09:00)"
                    >
                      ⚡ Past 24h (09:00 - 09:00)
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {(group.windows && group.windows.length > 0 ? group.windows : [newWindow()]).map((win, widx) => (
                    <div key={widx} style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                      <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                        <span style={{ fontSize: 12, color: "#64748b", marginBottom: 2 }}>Start Time</span>
                        <input
                          type="time"
                          className="eff-time-input"
                          value={win.start}
                          onChange={(e) => setWindow(gidx, widx, "start", e.target.value)}
                          onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                          style={{ ...textInputStyle, width: 140, cursor: "pointer" }}
                        />
                      </label>
                      <label style={{ ...inputGroupStyle, flex: "0 0 auto" }}>
                        <span style={{ fontSize: 12, color: "#64748b", marginBottom: 2 }}>End Time</span>
                        <input
                          type="time"
                          className="eff-time-input"
                          value={win.end}
                          onChange={(e) => setWindow(gidx, widx, "end", e.target.value)}
                          onClick={(e) => e.currentTarget.showPicker && e.currentTarget.showPicker()}
                          style={{ ...textInputStyle, width: 140, cursor: "pointer" }}
                        />
                      </label>
                      {win.last_sent && (
                        <span style={{ fontSize: 12, color: "#94a3b8", alignSelf: "center" }}>
                          Last sent: {win.last_sent}
                        </span>
                      )}
                      <div style={{ marginLeft: "auto" }}>
                        <button
                          type="button"
                          onClick={() => removeWindow(gidx, widx)}
                          disabled={(group.windows || []).length <= 1}
                          style={{
                            ...actionButtonStyle,
                            color: (group.windows || []).length <= 1 ? "#cbd5e1" : "#ef4444",
                            cursor: (group.windows || []).length <= 1 ? "not-allowed" : "pointer",
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => addWindow(gidx)}
                  style={{
                    marginTop: 8,
                    background: "none",
                    border: "1px dashed #cbd5e1",
                    borderRadius: 8,
                    padding: "6px 12px",
                    fontSize: 12,
                    color: "#334155",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  + Add Time Window
                </button>
              </div>
            ) : (
              <div
                style={{
                  marginTop: 14,
                  padding: "10px 14px",
                  background: "#f0fdfa",
                  border: "1px solid #ccfbf1",
                  borderRadius: 8,
                  fontSize: 13,
                  color: "#0f766e",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span>ℹ️</span>
                <span>
                  <strong>Full Period Summary:</strong> Weekly, 15-Day, and Monthly reports automatically cover the full duration and are delivered at 09:00 AM. No time windows needed.
                </span>
              </div>
            )}
          </div>
        );
      })}

      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
        <button
          type="button"
          onClick={addGroup}
          style={{
            background: "none",
            border: "1px dashed #cbd5e1",
            borderRadius: 8,
            padding: "8px 16px",
            fontSize: 13,
            color: "#334155",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          + Add Sensor Schedule
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          style={{
            ...primaryButtonStyle,
            opacity: saving ? 0.6 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
        >
          {saving ? "Saving..." : "Save Environment Schedule"}
        </button>
      </div>
    </div>
  );
}
